import { css } from '@emotion/react'
import { format } from 'date-fns'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef, Fragment } from 'react'

import { useAlumnaeArticleQuery } from '@/features/article-templates'
import { DatoStructuredText } from '@/features/common/'
import { GatsbyImageFocused } from '@/features/common/'
import { DatoLinkIcon } from '@/features/dato-link'
import { LightboxLink } from '@/features/lightbox'
import { aspectRatio, bezier, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentPropsWithoutRef<'div'> {
  data?: Queries.AlumnaeArticleFeaturedThumbnailFragment | null
}

export const AlumnaeArticleFeaturedThumbnail = ({
  data,
  ...props
}: Props): JSX.Element => {
  const article = useAlumnaeArticleQuery(data?.slug)
  const getPublicationDate = () => {
    const date = new Date(data?.meta.createdAt || '')
    return format(date, 'MMMM d, y')
  }

  const styles = {
    container: css`
      position: relative;
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: 1fr 2fr;
      background: ${colors.gray20};
      color: #fff;
      ${mq().ms} {
        grid-template-columns: 1fr;
      }
    `,
    image: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      grid-column: 1 / 2;
      grid-row: 1 / 3;
      overflow: hidden;
      ${mq().ms} {
        position: relative;
        grid-row: auto;
        ${aspectRatio(3 / 2)}
      }
      [data-gatsby-image-wrapper] {
        height: 100%;
      }
    `,
    text: css`
      grid-column: 2 / 3;
      align-self: center;
      padding: 3em var(--gtr-54);
      ${mq().ms} {
        grid-column: auto;
      }
    `,
    category: css`
      display: inline-block;
      text-transform: uppercase;
      font-size: var(--fs-16);
      font-weight: 450;
      color: ${colors.redLight};
    `,
    divider: css`
      color: rgba(255, 255, 255, 0.25);
    `,
    date: css`
      display: inline-block;
      text-transform: uppercase;
      font-size: var(--fs-16);
      font-weight: 300;
      color: rgba(255, 255, 255, 0.75);
    `,
    heading: css`
      font-size: var(--fs-60);
      margin: 0.25em 0 0;
    `,
    alumna: css`
      font-size: var(--fs-36);
      margin: 0.25em 0 0.5em;
      color: rgba(255, 255, 255, 0.75);
      &:after {
        content: '';
        display: block;
        width: 1em;
        margin-top: 0.5em;
        height: 2px;
        background: ${colors.redLight};
      }
    `,
    body: css`
      font-weight: 350;
      margin-top: 2em;
    `,
    link: css`
      position: relative;
      --font-weight: 500;
      font-weight: var(--font-weight);
      display: block;
      margin: 1.5em 0 0;
      padding: 0.75em 1.5em 0.75em 0.75em;
      max-width: fit-content;
      text-transform: uppercase;
      text-decoration: none;
      color: #fff;
      border: 2px #fff solid;
      transition:
        color 200ms ease,
        border-color 200ms ease;
      line-height: 1;
      span {
        position: relative;
      }
      &:before {
        content: '';
        position: absolute;
        height: 100%;
        width: 0%;
        top: 0;
        left: 0;
        background: ${colors.redLight};
        transition: width 200ms ${bezier.easeOut};
      }
      @media (hover: hover) {
        &:hover {
          color: #fff;
          border-color: ${colors.redLight};
          &:before {
            width: 100%;
          }
          && svg {
            transform: translate3d(0, 0, 0);
          }
        }
      }
    `,
  }
  if (article) {
    return (
      <div
        css={styles.container}
        {...props}
      >
        <GatsbyImageFocused
          css={styles.image}
          image={data?.featuredThumbnailImage?.gatsbyImageData}
          aspectRatio={1 / 1}
          data={data?.thumbnailImage}
        />
        <div css={styles.text}>
          <div css={styles.category}>{data?.category?.name}</div>
          <span css={styles.divider}>&emsp;|&emsp;</span>
          <div css={styles.date}>{getPublicationDate()}</div>
          <h2 css={styles.heading}>{data?.heading}</h2>
          <h3 css={styles.alumna}>
            {data?.alumna?.[0]?.name} ’
            {data?.alumna?.[0]?.graduationYear?.slice(-2)}
          </h3>
          <div css={styles.body}>
            <DatoStructuredText data={data?.excerpt} />
          </div>
          <LightboxLink
            data={article}
            css={styles.link}
          >
            <span>Read The Story</span>
            <DatoLinkIcon iconType="LIGHTBOX" />
          </LightboxLink>
        </div>
      </div>
    )
  } else {
    return <Fragment />
  }
}

export const AlumnaeArticleFeaturedThumbnailFragment = graphql`
  fragment AlumnaeArticleFeaturedThumbnail on DatoCmsAlumnaeArticle {
    ...AlumnaeArticleThumbnail
    featuredThumbnailImage: thumbnailImage {
      gatsbyImageData(
        width: 720
        sizes: "(max-width: 720px) 100vw, 50vw"
        imgixParams: { ar: "1:1", fit: "crop", crop: "focalpoint" }
      )
      ...ImageFocalData
    }
    excerpt {
      value
    }
  }
`
